import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { StatusBadge, Badge } from '../components/ui/Badge'
import { 
  ArrowLeft,
  Send,
  Upload,
  Eye,
  Download,
  FileText,
  CheckCircle,
  AlertTriangle,
  XCircle,
  MessageCircle,
  Paperclip,
  Settings
} from 'lucide-react'

interface Requirement {
  id: string
  name: string
  status: 'met' | 'partially_met' | 'not_met'
}

interface FileItem {
  id: string
  name: string
  type: string
  size: string
}

export const ResponseViewerPage: React.FC = () => {
  const { responseId } = useParams()
  const [activeTab, setActiveTab] = useState<'chat' | 'requirements' | 'files'>('chat')
  const [chatMessage, setChatMessage] = useState('')
  const [chatMessages, setChatMessages] = useState([
    {
      id: '1',
      role: 'assistant',
      content: 'This response is missing the following mandatory items: Insurance certificate, Conflict of interest disclosure, Detailed project timeline. You can ask the AI to draft a follow-up email to the vendor requesting these documents.',
      timestamp: new Date().toISOString()
    }
  ])

  const requirements: Requirement[] = [
    { id: '1', name: 'Functional', status: 'met' },
    { id: '2', name: 'Infrastructure Details', status: 'partially_met' },
    { id: '3', name: 'Performance', status: 'not_met' },
    { id: '4', name: 'Change Management', status: 'met' },
    { id: '5', name: 'Financials', status: 'met' },
    { id: '6', name: 'Project Schedule', status: 'met' },
  ]

  const files: FileItem[] = [
    { id: '1', name: 'Technical Response.pdf', type: 'PDF', size: '2.5 MB' },
    { id: '2', name: 'Financial Response.pdf', type: 'PDF', size: '1.8 MB' },
    { id: '3', name: 'Company Profile.pdf', type: 'PDF', size: '3.2 MB' },
  ]

  const suggestions = [
    'Compare this response to the others, where does it stand out',
    'Summarize what\'s missing in an email draft',
    'Create section about...'
  ]

  const handleSendMessage = () => {
    if (!chatMessage.trim()) return
    
    const newMessage = {
      id: Date.now().toString(),
      role: 'user' as const,
      content: chatMessage,
      timestamp: new Date().toISOString()
    }
    
    setChatMessages([...chatMessages, newMessage])
    setChatMessage('')
    
    // Simulate AI response
    setTimeout(() => {
      const aiResponse = {
        id: (Date.now() + 1).toString(),
        role: 'assistant' as const,
        content: 'I understand your question. Based on the response analysis, here\'s what I found...',
        timestamp: new Date().toISOString()
      }
      setChatMessages(prev => [...prev, aiResponse])
    }, 1000)
  }

  const getRequirementIcon = (status: string) => {
    switch (status) {
      case 'met':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'partially_met':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />
      case 'not_met':
        return <XCircle className="w-4 h-4 text-red-600" />
      default:
        return null
    }
  }

  const getRequirementBadge = (status: string) => {
    switch (status) {
      case 'met':
        return <Badge variant="success">Yes</Badge>
      case 'partially_met':
        return <Badge variant="warning">Partially</Badge>
      case 'not_met':
        return <Badge variant="destructive">No</Badge>
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-1">
      {/* PDF Viewer Panel */}
      <div className="flex-1 bg-white">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Project
            </Button>
            <div className="h-6 w-px bg-gray-300" />
            <h1 className="text-lg font-semibold text-gray-900">HCLTech - Technical Proposal</h1>
          </div>
          <Button variant="ghost" size="sm">
            <Settings className="w-4 h-4" />
          </Button>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 p-4">
          <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">PDF viewer would be displayed here</p>
              <p className="text-sm text-gray-500 mt-2">Technical Proposal.pdf</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Chat/Requirements/Files */}
      <div className="w-96 bg-white border-l flex flex-col">
        {/* Tab Navigation */}
        <div className="flex border-b">
          <button
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'chat'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('chat')}
          >
            AI Chat
          </button>
          <button
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'requirements'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('requirements')}
          >
            Requirements
          </button>
          <button
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'files'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('files')}
          >
            Files
          </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 flex flex-col">
          {/* AI Chat Tab */}
          {activeTab === 'chat' && (
            <>
              {/* Chat Messages */}
              <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                {chatMessages.map((message) => (
                  <div key={message.id} className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        {message.role === 'assistant' ? (
                          <span className="text-purple-600 font-bold text-sm">P</span>
                        ) : (
                          <span className="text-gray-600 font-bold text-sm">U</span>
                        )}
                      </div>
                    </div>
                    <div className="ml-10 bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-700 whitespace-pre-line">
                        {message.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Suggestions */}
              <div className="px-4 py-2 border-t bg-gray-50">
                <p className="text-xs font-medium text-gray-600 mb-2">Suggestions</p>
                <div className="space-y-2">
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-white rounded border cursor-pointer hover:bg-gray-50"
                      onClick={() => setChatMessage(suggestion)}
                    >
                      <span className="text-sm text-gray-700">{suggestion}</span>
                      <button className="text-gray-400 hover:text-gray-600">
                        <Settings className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Chat Input */}
              <div className="p-4 border-t">
                <div className="flex items-center space-x-2 p-2 border rounded-lg">
                  <input
                    type="text"
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    placeholder="Ask Propalyst for anything..."
                    className="flex-1 outline-none text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  />
                  <button className="text-gray-400 hover:text-gray-600">
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <Button
                    size="sm"
                    className="bg-purple-600 text-white hover:bg-purple-700"
                    onClick={handleSendMessage}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Requirements Tab */}
          {activeTab === 'requirements' && (
            <div className="flex-1 p-4">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">6 Requirements</h3>
                <div className="bg-gray-100 rounded-lg p-3">
                  <div className="grid grid-cols-3 gap-4 text-xs font-medium text-gray-600 uppercase">
                    <span>Parameters</span>
                    <span>Meets Requirement</span>
                    <span>Action</span>
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                {requirements.map((requirement) => (
                  <div key={requirement.id} className="grid grid-cols-3 gap-4 items-center p-3 border-b">
                    <span className="text-sm text-gray-900">{requirement.name}</span>
                    <div className="flex items-center space-x-2">
                      {getRequirementIcon(requirement.status)}
                      {getRequirementBadge(requirement.status)}
                    </div>
                    <Button variant="ghost" size="sm" className="text-xs">
                      {requirement.status === 'not_met' ? 'Ask Darwin' : 'Jump to Requirement'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Files Tab */}
          {activeTab === 'files' && (
            <div className="flex-1 p-4">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">3 Files</h3>
                <Button size="sm" variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload file
                </Button>
              </div>

              <div className="bg-gray-100 rounded-lg p-3 mb-4">
                <div className="grid grid-cols-2 gap-4 text-xs font-medium text-gray-600 uppercase">
                  <span>File name</span>
                  <span>Action</span>
                </div>
              </div>

              <div className="space-y-1">
                {files.map((file) => (
                  <div key={file.id} className="grid grid-cols-2 gap-4 items-center p-3 border-b">
                    <div>
                      <span className="text-sm text-gray-900">{file.name}</span>
                      <p className="text-xs text-gray-500">{file.size}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <FileText className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ResponseViewerPage